import { useState, useEffect, useRef } from 'react';
import * as S from './styles';
import { saveAs } from 'file-saver';
import Qrcode from "react-qr-code";
import Barcode from "react-barcode";
import JSZip from 'jszip';
//Icons
import { X } from '@phosphor-icons/react';
import { CurrencyFormat } from 'utils/CurrencyFormat';
import React from 'react';
import html2canvas from 'html2canvas';
import ReactLoading from 'react-loading';
//hooks
import useLoading from '../../hooks/useLoading';

interface ITagItem {
    barcode: string;
    code: string;
    brands?: string | null;
    price: string;
}

interface IBarcode {
    isOpenBarcode: boolean;
    setModalClose: () => void;
    itemsTag?: ITagItem[]; // Propriedade para suportar múltiplos itens
}

export default function BarcodeTeste({
    isOpenBarcode,
    setModalClose,
    itemsTag = []
}: IBarcode) {
    const targetRef = useRef<HTMLDivElement>(null);
    // const [selectedType, setSelectedType] = useState<'qrcode' | 'barcode'>('barcode');

    const [selectedType, setSelectedType] = useState("barcode");
    const tagRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);
    const { isLoading, startLoading, stopLoading } = useLoading();
    const [sizeType, setSizeType] = useState("large"); // "large" (G) ou "small" (p)

    const handleSelectType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const typeCheck = event.target.value;
        const [codeType, size] = typeCheck.split("-");

        setSelectedType(codeType);
        setSizeType(size === "p" ? "small" : "large");
    };

    // Inicialize as refs quando itemsTag mudar
    useEffect(() => {
        // Criar uma ref para cada item
        tagRefs.current = itemsTag.map(() => React.createRef<HTMLDivElement>());
    }, [itemsTag]);

    // Função para capturar uma etiqueta como PNG
    const captureTagAsPng = async (ref: React.RefObject<HTMLDivElement>) => {
        if (!ref.current) return null;

        try {
            const canvas = await html2canvas(ref.current, {
                scale: 2, // Aumentar a qualidade
                backgroundColor: null, // Transparente
                useCORS: true // Permitir imagens de outros domínios
            });

            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error('Erro ao capturar etiqueta:', error);
            return null;
        }
    };

    // Função para baixar uma etiqueta específica
    const downloadTag = async (index: number) => {
        const item = itemsTag[index];
        if (!item || !tagRefs.current[index]) return;

        const imgData = await captureTagAsPng(tagRefs.current[index]);
        if (!imgData) return;

        const link = document.createElement("a");
        link.href = imgData;
        const fileName = `${selectedType}_${item.brands ? `${item.brands}_` : ''}${item.code.replace(/\s+/g, '_')}.png`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Função para baixar todas as etiquetas em um arquivo ZIP
    const downloadAllTags = async () => {
        startLoading();

        if (!itemsTag?.length) return;

        // Criar uma nova instância do JSZip
        const zip = new JSZip();

        // Capturar e adicionar cada PNG ao arquivo zip
        for (let i = 0; i < itemsTag.length; i++) {
            const item = itemsTag[i];
            const imgData = await captureTagAsPng(tagRefs.current[i]);

            if (imgData) {
                // Remover o cabeçalho "data:image/png;base64," para obter apenas os dados
                const base64Data = imgData.replace(/^data:image\/png;base64,/, '');

                // Criar nome de arquivo para cada etiqueta
                const fileName = `${selectedType}_${item.brands ? `${item.brands}_` : ''}${item.code.replace(/\s+/g, '_')}.png`;

                // Adicionar a imagem PNG ao arquivo zip
                zip.file(fileName, base64Data, { base64: true });
            }
        }

        try {
            // Gerar o conteúdo do arquivo zip
            const content = await zip.generateAsync({ type: 'blob' });

            // Baixar o arquivo zip
            saveAs(content, 'etiquetas.zip');
        } catch (error) {
            console.error('Erro ao gerar arquivo ZIP:', error);
            alert('Ocorreu um erro ao gerar o arquivo ZIP. Tente novamente.');
        } finally {
            stopLoading();
        }
    };

    const OnClose = () => {
        setModalClose();
        itemsTag = [];
    }

    // Componente de etiqueta
    const SmallLabel = ({ item }: any) => (
        <S.PrintQrcodeSmall ref={targetRef}>
            <S.CardTag isLarge={sizeType === 'large'}>
                <S.LeftSection>
                    {selectedType === 'barcode' ? (
                        <Barcode value={item.barcode} height={sizeType === 'small' ? 45 : 75} width={sizeType === 'small' ? 1 : 1.5} fontSize={10} />
                    ) : (
                        <Qrcode value={item.code} size={sizeType === 'small' ? 60 : 90} />
                    )}
                </S.LeftSection>
                <S.RightSection>
                    <S.LabelName isLarge={sizeType === 'large'}>{item.code}</S.LabelName>
                    <S.LabelName isLarge={sizeType === 'large'}>{item.brands || item.barcode}</S.LabelName>
                    <S.LabelName isLarge={sizeType === 'large'}>{CurrencyFormat(parseFloat(item.price || '0'))}</S.LabelName>
                </S.RightSection>
            </S.CardTag>
        </S.PrintQrcodeSmall>
    );

    if (isOpenBarcode) {
        return (
            <S.Container>
                <S.Content>

                    <S.Header>
                        <S.Title>Gerador de Etiquetas</S.Title>
                        <X size={24} onClick={OnClose} />
                    </S.Header>


                    <S.Controls>
                        <S.Label>Selecione o tamanho da etiqueta:</S.Label>
                        <S.SelectTag onChange={(e) => handleSelectType(e)}>
                            <option value="barcode-g">Código de Barras (G)</option>
                            <option value="qrcode-g">QRcodes (G)</option>
                            <option value="barcode-p">Código de Barras (p)</option>
                            <option value="qrcode-p">QRcodes (p)</option>
                        </S.SelectTag>
                    </S.Controls>

                    <S.TagsGrid>
                        {itemsTag?.map((item, index) => (
                            <S.TagCard key={`${item.barcode || 0}-${index}`}>
                                <S.TagPreview ref={tagRefs.current[index]}>
                                    <SmallLabel item={item} />
                                </S.TagPreview>

                                <S.DownloadButton
                                    onClick={() => downloadTag(index)}
                                >
                                    Baixar
                                </S.DownloadButton>
                            </S.TagCard>
                        ))}
                    </S.TagsGrid>

                    {itemsTag?.length === 0 && (
                        <S.EmptyState>
                            Nenhum item para gerar etiqueta
                        </S.EmptyState>
                    )}

                    <S.Footer>
                        <S.Button
                            onClick={downloadAllTags}
                            disabled={!itemsTag?.length}
                        >
                            {
                                isLoading &&
                                <ReactLoading type="spokes" color="white" height="20px" width="20px" />
                            }
                            Baixar Todas as Etiquetas
                        </S.Button>
                    </S.Footer>
                </S.Content>
            </S.Container>
        );
    }
    return null;
}